/** @jsx jsx */
import React, { useState, useEffect, useRef } from "react";
import { Box, jsx, Text as ReactText } from "theme-ui";
import { Container } from "@shared-components/Container/Container";
import { Heading } from "@shared-components/Heading/Heading";
import { Grid } from "@shared-components/Grid/Grid";
import {
  headingStylesCollection,
  textStylesCollection,
} from "../missionHybridSlider/MultiSliderModule/styles";
import { Card } from "./Card";
import { BottleDetails } from "@modules/BottleDetails/BottleDetails";
import { Portal } from "@shared-components/Portal/Portal";
import { LayoutContainer } from "@shared-components/LayoutContainer/LayoutContainer";
import { generateCbKey } from "../../shared/utils/generate-cbKey";
import { Text as TextModule } from "@modules/Text/Text";
import { VideoLinkModule } from "./VideoLinkModule/VideoLinkModule";
import { MultiSliderModule } from "@modules/missionHybridSlider/MultiSliderModule/MultiSliderModule";
import {
  AlgoliaBottle,
  Memories,
} from "../../carlsberg-headless/types/modules/digital-memories";
import { useDictionaryContext } from "../../context/dictionary/useDictionaryContext";
import { DICTIONARY_MAP } from "../../carlsberg-headless/dictionary-map";
import { useSiteContext } from "../../context/site/useSiteContext";
import { FlavourModule } from "./Flavour/Flavour";
import { Media } from "@modules/Media/Media";
import { ButtonLink } from "@shared-components/Button/Button";
import { CodeModule } from "./CodeModule/CodeModule";
import { Icon } from "@shared-components/Icon/Icon";
import LoadingIcon from "@svg-icons/leaf-loader.component.svg";
import { VideoPlayerProvider } from "./VideoPlayer/VideoPlayerContext";
import { Text } from "@shared-components/Text/Text";
import { HOC_SESSIONS } from "../../shared/constants/HOCSessionName";

interface MyBottleCollectionProps {}

function hexToString(hex: any) {
  // REVERSE stringToHex to test id's from firebase
  let str = "";
  for (let i = 0; i < hex.length; i += 2) {
    const hexValue = hex.substr(i, 2);
    const decimalValue = parseInt(hexValue, 16);
    str += String.fromCharCode(decimalValue);
  }
  return str.split("\x00")[0];
}

const stringToHex = (str: string) => {
  let hex = "";
  for (let i = 0; i < str.length; i++) {
    const charCode = str.charCodeAt(i);
    const hexValue = charCode.toString(16);

    // Pad with zeros to ensure two-digit representation
    hex += hexValue.padStart(2, "0");
  }
  if (hex.length < 24) {
    for (let i = hex.length; i < 24; i++) {
      hex += "0";
    }
  }
  return hex;
};

// @ts-ignore-next-line
globalThis.hexToString = hexToString;
// @ts-ignore-next-line
globalThis.stringToHex = stringToHex;

//EXAMPPLE
// window.hexToString("452d3132382d373830000000") // = E-128-780

const GoToKioskModule = ({
  texts,
}: {
  texts: {
    headline: string;
    linkText: string;
    linkUrl: string;
  };
}) => {
  return (
    <LayoutContainer>
      <Media
        cbKey={generateCbKey()}
        alias="mediaModuleElement"
        key="9d0ee29d-18d9-44b3-b717-3693babf1a9f"
        image={{
          alias: "imageAsset",
          url: "/media/45gfjkhi/rectangle-148-1.png",
          height: 1468,
          width: 2768,
          extension: null,
          size: 2917366,
          fullUrl:
            "https://cdne-cmh-hoc-dev.azureedge.net/media/45gfjkhi/rectangle-148-1.png",
          altText: texts.headline,
          focalPoint: {
            left: 0.5,
            top: 0.5,
          },
        }}
        link={{
          name: texts.linkText,
          urlSegment: "kiosk",
          url: texts.linkUrl,
          relativeUrl: texts.linkUrl,
          target: "",
        }}
        video={null}
        taxonomies={["bottle_collection", "layout_spacing_none"]}
        shortCodes={[]}
        themes={[]}
      />
    </LayoutContainer>
  );
};

const MyBottleCollection: React.FC<MyBottleCollectionProps> = () => {
  const [collection, setCollection] = useState<Memories | null>(null);
  const [bottles, setBottles] = useState<AlgoliaBottle[]>([]);
  const [videos, setVideos] = useState<string[]>([]);
  const [flavours, setFlavours] = useState<[]>([]);
  const [process, setProcess] = useState<any>(null);
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [outofdate, setOutofdate] = useState<boolean>(false);
  const [modulesArray, setArray] = useState<
    { module: string; location: number }[]
  >([]);
  const [modulesCounter, setCounter] = useState<number>(0);
  const [currentBottleIndex, setCurrentBottleIndex] = useState<number>(0);
  const { get } = useDictionaryContext();
  const { currentCulture } = useSiteContext();
  const [scrollY, setScrollY] = useState(0);
  const ref = useRef();

  let bottleId = undefined;
  let user = undefined;

  const getScrollbarWidth = () => {
    return window.innerWidth - document.documentElement.clientWidth;
  };

  const handleDisableScroll = (disableScroll: boolean, scrollY: number) => {
    const htmlTag = document.documentElement;
    const bodyTag = document.body;
    const scrollBarWidth = getScrollbarWidth();
    if (disableScroll) {
      htmlTag.style.overflow = "hidden";
      htmlTag.style.position = "fixed";
      htmlTag.style.width = "100%";
      htmlTag.style.marginTop = `-${scrollY}px`;
      bodyTag.style.marginRight = `${scrollBarWidth}px`;
      bodyTag.style.position = "relative";
    }

    if (!disableScroll) {
      htmlTag.removeAttribute("style");
      bodyTag.removeAttribute("style");
      window.scrollTo(0, scrollY);
    }
  };

  useEffect(() => {
    function getCollectionData() {
      if (user) {
        const hexedUser = stringToHex(user);
        try {
          fetch(
            `https://europe-west3-cb-digital-memories.cloudfunctions.net/dm/u/${hexedUser}`
          )
            .then((response) => response.json())
            .then((data) => {
              setLoading(false);
              if (
                data.data &&
                data.data["A8.10AV01"] &&
                data.data["A8.10AV01"].bottles
              ) {
                const bottleArray = [] as AlgoliaBottle[];
                const collectionBottles = Object.values(
                  data.data["A8.10AV01"].bottles
                );
                collectionBottles.map((id) => {
                  try {
                    fetch(
                      `https://europe-west3-cb-bottle-collection.cloudfunctions.net/bottles/api/${id}`
                    )
                      .then((response) => response.json())
                      .then((data) => {
                        bottleArray.push(data);
                        if (collectionBottles.length === bottleArray.length) {
                          setBottles(bottleArray);
                        }
                      });
                  } catch (err) {
                    console.log("err", err);
                  }
                });
              }
              if (data.data["A5.8AV01"] && data.data["A5.8AV01"]) {
                const array = [] as any;
                data.data["A5.8AV01"].map((videoData: { Played: string }) => {
                  if (videoData.Played && !array.includes(videoData.Played)) {
                    array.push(videoData.Played);
                  }
                });
                setVideos(array);
              }

              setCollection(data.data);
              Object.keys(data.data).map((id) => {
                if (id === "A3") {
                  setArray((old) => [...old, { module: id, location: 1 }]);
                  setCounter(modulesCounter + 1);
                }
                if (id === "A5.6AV01") {
                  setArray((old) => [...old, { module: id, location: 2 }]);
                  setCounter(modulesCounter + 1);
                }
                if (id === "A5.5AV01") {
                  setArray((old) => [...old, { module: id, location: 3 }]);
                  setCounter(modulesCounter + 1);
                }
                if (id === "A5.7AV01") {
                  setArray((old) => [...old, { module: id, location: 4 }]);
                  setCounter(modulesCounter + 1);
                }
                if (id === "A5.8AV01") {
                  setArray((old) => [...old, { module: id, location: 5 }]);
                  setCounter(modulesCounter + 1);
                }
                if (id === "A8.10AV01") {
                  if (data.data["A8.10AV01"].labels) {
                    setArray((old) => [...old, { module: id, location: 6 }]);
                    setCounter(modulesCounter + 1);
                  }
                }
                if (id === "A1000") {
                  setArray((old) => [...old, { module: id, location: 7 }]);
                  setCounter(modulesCounter + 1);
                }
                if (id === "A9") {
                  setArray((old) => [...old, { module: id, location: 8 }]);
                  setCounter(modulesCounter + 1);
                }
              });
            })
            .catch((error) => {
              setLoading(false);
              setOutofdate(true);
              console.log("error", error);
            });
        } catch (error) {
          console.log("error", error);
        }
      }
    }
    getCollectionData();
  }, []);

  useEffect(() => {
    if (bottleId) {
      if (bottles && bottles.length !== 0) {
        const bottleIndex = bottles.findIndex(
          (bottle) => bottle.id.toString() === bottleId
        );

        if (bottleIndex >= 0) {
          setCurrentBottleIndex(bottleIndex);
          setShow(true);
          handleDisableScroll(true, 0);
        } else {
          history.replaceState(
            null,
            "",
            `${window.location.toString().split("?")[0]}#${user}`
          );
        }
      }
    }
  }, [bottles]);

  useEffect(() => {
    if (collection && collection["A5.6AV01"]) {
      let items = [] as any;
      collection["A5.6AV01"]?.map((choise) => {
        if (choise.items) {
          choise.items.map((item) => {
            const doesExist = items.includes(item.id);
            if (!doesExist) {
              items.push(item.id);
            }
          });
        } else {
          const doesExist = items.includes(choise.item);
          if (!doesExist) {
            items.push(choise.item);
          }
        }
      });

      setFlavours(items);
    }

    // Process level
    if (collection?.["A5.7AV01"]) {
      let bestMatch = null;
      bestMatch = collection["A5.7AV01"].find(
        (p) => p.Level === "MASTERBREWER"
      )?.Level;
      if (!bestMatch) {
        bestMatch = collection["A5.7AV01"].find(
          (p) => p.Level === "BREWER"
        )?.Level;
      }
      if (!bestMatch) {
        bestMatch = collection["A5.7AV01"].find(
          (p) => p.Level === "APPRENTICE"
        )?.Level;
      }

      switch (bestMatch) {
        case "APPRENTICE":
          setProcess({
            format: "mp4",
            src: get(
              DICTIONARY_MAP.digital_memories
                .digital_memories_process_video_apprentice,
              currentCulture
            ),
            noSound: true,
          });
        case "BREWER":
          setProcess({
            format: "mp4",
            src: get(
              DICTIONARY_MAP.digital_memories
                .digital_memories_process_video_brewer,
              currentCulture
            ),
            noSound: true,
          });
          break;
        case "MASTERBREWER":
          setProcess({
            format: "mp4",
            src: get(
              DICTIONARY_MAP.digital_memories
                .digital_memories_process_video_masterbrewer,
              currentCulture
            ),
            noSound: true,
          });
          break;
      }
    }
  }, [collection]);

  useEffect(() => {
    const newArray = [...modulesArray].sort((a, b) => a.location - b.location);
    setArray(newArray);
  }, [modulesCounter]);

  const openDetail = (index: number, id: string) => {
    // eslint-disable-next-line no-restricted-globals
    history.pushState(
      null,
      "",
      `${window.location.toString().split("#")[0]}?bottle=${id}#${user}`
    );
    setScrollY(window?.scrollY);
    handleDisableScroll(true, window?.scrollY);
    setShow(true);
    setCurrentBottleIndex(index);
  };

  const toggleDetail = () => {
    handleDisableScroll(!show, scrollY);
    setShow(!show);
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const bottleId = params.get("bottle");

    // eslint-disable-next-line no-restricted-globals
    if (bottleId && bottles.length > 0) {
      const bottleIndex = bottles.findIndex(
        (bottle: any) => bottle.id === bottleId
      );

      bottleIndex && setCurrentBottleIndex(bottleIndex);
      setShow(true);
      setScrollY(window?.scrollY);
      handleDisableScroll(true, window?.scrollY);
    }
  }, [bottles]);

  const handleBottleIndex = (direction: "next" | "prev" | undefined) => {
    let newId;

    if (direction === "next") {
      if (!bottles) {
        return;
      }
      if (currentBottleIndex + 1 !== bottles.length) {
        setCurrentBottleIndex((prevIndex) => prevIndex + 1);
        newId =
          bottles[currentBottleIndex + 1] && bottles[currentBottleIndex + 1].id;
        // eslint-disable-next-line no-restricted-globals
        history.replaceState(
          null,
          "",
          `${window.location.toString().split("?")[0]}?bottle=${newId}#${user}`
        );
      }
    } else if (direction === "prev" && currentBottleIndex - 1 !== -1) {
      setCurrentBottleIndex((prevIndex) => prevIndex - 1);
      newId = bottles[currentBottleIndex - 1].id;
      // eslint-disable-next-line no-restricted-globals
      history.replaceState(
        null,
        "",
        `${window.location.toString().split("?")[0]}?bottle=${newId}#${user}`
      );
    }
  };

  const futureMedia = videos?.map((played: string) => {
    switch (played) {
      case "Hops":
        return {
          format: "mp4",
          src: get(
            DICTIONARY_MAP.digital_memories
              .digital_memories_future_video_1_link,
            currentCulture
          ),
          link: get(
            DICTIONARY_MAP.digital_memories.digital_memories_future_link_1,
            currentCulture
          ),
          videoId: played,
          noSound: true,
        };
      case "Yeast":
        return {
          format: "mp4",
          src: get(
            DICTIONARY_MAP.digital_memories
              .digital_memories_future_video_2_link,
            currentCulture
          ),
          link: get(
            DICTIONARY_MAP.digital_memories.digital_memories_future_link_2,
            currentCulture
          ),
          videoId: played,
          noSound: true,
        };
      case "Malt":
        return {
          format: "mp4",
          src: get(
            DICTIONARY_MAP.digital_memories
              .digital_memories_future_video_3_link,
            currentCulture
          ),
          link: get(
            DICTIONARY_MAP.digital_memories.digital_memories_future_link_3,
            currentCulture
          ),
          videoId: played,
          noSound: true,
        };
      default:
        return;
    }
  });

  (() => {
    if (ref.current && modulesArray.length % 2 === 0) {
      const element = ref.current as any;
      const nextElement = element.parentElement.parentElement.nextSibling;
      if (
        nextElement &&
        nextElement.style.backgroundColor.toString() === "" &&
        nextElement.firstChild.firstChild.id !== "newsletter"
      ) {
        nextElement.style.backgroundColor = "#FFF";
      }
    }
  })();

  if (typeof window === "undefined") return null;
  if (typeof globalThis === "undefined") return null;
  if (!globalThis.document) return null;

  user = window.location.hash.split("#")[1];
  const params = new URLSearchParams(window.location.search);
  bottleId = params.get("bottle");

  if (user) {
    return (
      <Box ref={ref}>
        {loading && !collection && (
          <div
            sx={{
              width: "200px",
              height: "200px",
              py: "200px",
              margin: "0 auto",
              textAlign: "center",
            }}
          >
            <Text
              sx={{
                color: "darkGreen",
                fontSize: "paragraphSmall",
                textTransform: "uppercase",
                marginBottom: "10px",
                fontWeight: "bold",
              }}
            >
              {get(
                DICTIONARY_MAP.digital_memories.digital_memories_loading_text,
                currentCulture
              )}
            </Text>
            <Icon
              svg={LoadingIcon}
              sx={{
                width: "20px",
                height: "20px",
                margin: "0 auto",
                fill: "darkGreen",
                transformOrigin: "50% 45%",
                animation: "rotation 0.75s infinite linear",
              }}
            />
          </div>
        )}
        {collection && !loading && (
          <VideoPlayerProvider>
            <LayoutContainer
              themes={["layout_bg_green"]}
              taxonomies={["layout_spacing_lg"]}
            >
              <Container>
                <TextModule
                  whiteHeading
                  sectionTitle={""}
                  headline={
                    get(
                      DICTIONARY_MAP.digital_memories
                        .active_digital_memories_headline,
                      currentCulture
                    ) ?? null
                  }
                  content={
                    get(
                      DICTIONARY_MAP.digital_memories
                        .active_digital_memories_text,
                      currentCulture
                    ) ?? null
                  }
                  key={"there-are-bottles"}
                  link={null}
                  cbKey={generateCbKey()}
                  shortCodes={[]}
                  themes={["text_centering", "layout_bg_green"]}
                  taxonomies={[]}
                  alias="textModuleElement"
                />
              </Container>
            </LayoutContainer>
            <Box>
              {/* A3 */}
              {collection.A3 && collection.A3.length > 0 && (
                <MultiSliderModule
                  videoTitle={
                    get(
                      DICTIONARY_MAP.digital_memories
                        .digital_memories_mission_headline,
                      currentCulture
                    ) ?? ""
                  }
                  themes={
                    modulesArray.findIndex((obj) => obj.module === "A3") % 2 ==
                    0
                      ? ["layout_bg_lightergray"]
                      : ["layout_bg_white"]
                  }
                  heading={
                    get(
                      DICTIONARY_MAP.digital_memories
                        .digital_memories_mission_headline,
                      currentCulture
                    ) ?? ""
                  }
                  subHeading={
                    get(
                      DICTIONARY_MAP.digital_memories
                        .digital_memories_mission_text,
                      currentCulture
                    ) ?? ""
                  }
                  slides={collection.A3.map((mission) => {
                    if (!mission.File || !mission.url) return null;
                    return {
                      format: mission.File.toString().split(".")[1],
                      src: mission.url,
                    };
                  })}
                  HOCSessionId={HOC_SESSIONS["A3"].eventName}
                  variant="mission"
                />
              )}

              {/* A5.6AV01 */}
              {collection["A5.6AV01"] && collection["A5.6AV01"].length > 0 && (
                <FlavourModule
                  themes={
                    modulesArray.findIndex((obj) => obj.module === "A5.6AV01") %
                      2 ==
                    0
                      ? ["layout_bg_lightergray"]
                      : ["layout_bg_white"]
                  }
                  bottleIds={flavours}
                />
              )}

              {/* A5.5AV01 */}
              {collection["A5.5AV01"] && collection["A5.5AV01"].length > 0 && (
                <MultiSliderModule
                  themes={
                    modulesArray.findIndex((obj) => obj.module === "A5.5AV01") %
                      2 ==
                    0
                      ? ["layout_bg_lightergray"]
                      : ["layout_bg_white"]
                  }
                  heading={
                    get(
                      DICTIONARY_MAP.digital_memories
                        .digital_memories_hybrid_headline,
                      currentCulture
                    ) ?? ""
                  }
                  subHeading={
                    get(
                      DICTIONARY_MAP.digital_memories
                        .digital_memories_hybrid_text,
                      currentCulture
                    ) ?? ""
                  }
                  slides={collection["A5.5AV01"].map((hybrid) => {
                    return {
                      format: "mp4",
                      src: hybrid.url,
                      downloadUrl: hybrid.url,
                    };
                  })}
                  variant="hybrid"
                  HOCSessionId={HOC_SESSIONS["A5.5AV01"].eventName}
                />
              )}

              {/* A5.7AV01 */}
              {process && (
                <MultiSliderModule
                  themes={
                    modulesArray.findIndex((obj) => obj.module === "A5.7AV01") %
                      2 ==
                    0
                      ? ["layout_bg_lightergray"]
                      : ["layout_bg_white"]
                  }
                  heading={
                    get(
                      DICTIONARY_MAP.digital_memories
                        .digital_memories_process_headline,
                      currentCulture
                    ) ?? ""
                  }
                  subHeading={
                    get(
                      DICTIONARY_MAP.digital_memories
                        .digital_memories_process_text,
                      currentCulture
                    ) ?? ""
                  }
                  slides={[process]}
                  variant="process"
                  HOCSessionId={HOC_SESSIONS["A5.7AV01"].eventName}
                />
              )}

              {/* A5.8AV01 */}
              {futureMedia && futureMedia.length > 0 && (
                <VideoLinkModule
                  themes={
                    modulesArray.findIndex((obj) => obj.module === "A5.8AV01") %
                      2 ==
                    0
                      ? ["layout_bg_lightergray"]
                      : ["layout_bg_white"]
                  }
                  heading={
                    get(
                      DICTIONARY_MAP.digital_memories
                        .digital_memories_future_headline,
                      currentCulture
                    ) ?? ""
                  }
                  subHeading={
                    get(
                      DICTIONARY_MAP.digital_memories
                        .digital_memories_future_text,
                      currentCulture
                    ) ?? ""
                  }
                  medias={futureMedia}
                  columns={3}
                />
              )}
            </Box>
            {bottles.length > 0 && (
              <Box sx={{ backgroundColor: "lightGray" }}>
                <Container
                  sx={{ backgroundColor: "lightGray", paddingBlock: "15" }}
                >
                  {/* TEXT */}
                  <Heading as={"h2"} sx={headingStylesCollection}>
                    {get(
                      DICTIONARY_MAP.digital_memories
                        .digital_memories_bottles_headline,
                      currentCulture
                    ) ?? ""}
                  </Heading>
                  <ReactText as={"h3"} sx={textStylesCollection}>
                    {get(
                      DICTIONARY_MAP.digital_memories
                        .digital_memories_bottles_text,
                      currentCulture
                    ) ?? ""}
                  </ReactText>
                  <Grid columns={[2, 3, 4]} gap={[2, 4, 4]}>
                    {bottles.map((beer: AlgoliaBottle, key: any) => {
                      return (
                        <Card
                          key={beer.id}
                          index={key}
                          {...beer}
                          onClick={() => openDetail(key, beer.id)}
                        >
                          {beer?.name}
                        </Card>
                      );
                    })}
                  </Grid>
                  <Box
                    sx={{ display: "flex", justifyContent: "center", mt: 10 }}
                  >
                    <ButtonLink
                      to={
                        get(
                          DICTIONARY_MAP.digital_memories
                            .digital_memories_bottles_link_url,
                          currentCulture
                        ) ?? ""
                      }
                      variant="green"
                      outline
                    >
                      {get(
                        DICTIONARY_MAP.digital_memories
                          .digital_memories_bottles_link_text,
                        currentCulture
                      ) ?? ""}
                    </ButtonLink>
                  </Box>
                </Container>
              </Box>
            )}

            {/* A8.10AV01 */}
            {collection["A8.10AV01"]?.labels &&
              collection["A8.10AV01"].labels.length > 0 && (
                <MultiSliderModule
                  themes={
                    modulesArray.findIndex(
                      (obj) => obj.module === "A8.10AV01"
                    ) %
                      2 ==
                    0
                      ? ["layout_bg_lightergray"]
                      : ["layout_bg_white"]
                  }
                  heading={
                    get(
                      DICTIONARY_MAP.digital_memories
                        .digital_memories_label_headline,
                      currentCulture
                    ) ?? ""
                  }
                  subHeading={
                    get(
                      DICTIONARY_MAP.digital_memories
                        .digital_memories_label_text,
                      currentCulture
                    ) ?? ""
                  }
                  slides={collection["A8.10AV01"].labels.map((label) => {
                    return {
                      format: "mp4",
                      src: label.url || "",
                      title: label.Caption + " " + label.Name,
                    };
                  })}
                  variant="label"
                  HOCSessionId={HOC_SESSIONS["A8.10AV01"].eventName}
                />
              )}

            {/* A1000 */}
            {collection["A1000"] && collection["A1000"].length > 0 && (
              <MultiSliderModule
                themes={
                  modulesArray.findIndex((obj) => obj.module === "A1000") % 2 ==
                  0
                    ? ["layout_bg_lightergray"]
                    : ["layout_bg_white"]
                }
                heading={
                  get(
                    DICTIONARY_MAP.digital_memories
                      .digital_memories_liverpool_headline,
                    currentCulture
                  ) ?? ""
                }
                subHeading={
                  get(
                    DICTIONARY_MAP.digital_memories
                      .digital_memories_liverpool_text,
                    currentCulture
                  ) ?? ""
                }
                slides={collection.A1000.filter((lab) => lab.url).map((lab) => {
                  return { format: "image/png", src: lab.url };
                })}
                variant="brew"
                HOCSessionId={HOC_SESSIONS["A1000"].eventName}
              />
            )}

            {/* A9 */}
            {collection["A9"] && collection["A9"].length > 0 && (
              <MultiSliderModule
                themes={
                  modulesArray.findIndex((obj) => obj.module === "A9") % 2 == 0
                    ? ["layout_bg_lightergray"]
                    : ["layout_bg_white"]
                }
                heading={
                  get(
                    DICTIONARY_MAP.digital_memories
                      .digital_memories_brewhouse_headline,
                    currentCulture
                  ) ?? ""
                }
                subHeading={
                  get(
                    DICTIONARY_MAP.digital_memories
                      .digital_memories_brewhouse_text,
                    currentCulture
                  ) ?? ""
                }
                slides={collection.A9.filter((lab) => lab.url).map((lab) => {
                  return { format: "image/png", src: lab.url };
                })}
                variant="brew"
                HOCSessionId={HOC_SESSIONS["A9"].eventName}
              />
            )}

            {(!bottles || bottles.length === 0) && (
              <GoToKioskModule
                texts={{
                  headline:
                    get(
                      DICTIONARY_MAP.digital_memories
                        .digital_memories_bottles_collection_headline,
                      currentCulture
                    ) ?? "",
                  linkText:
                    get(
                      DICTIONARY_MAP.digital_memories
                        .digital_memories_bottles_collection_link_text,
                      currentCulture
                    ) ?? "",
                  linkUrl:
                    get(
                      DICTIONARY_MAP.digital_memories
                        .digital_memories_bottles_collection_link_url,
                      currentCulture
                    ) ?? "",
                }}
              />
            )}
          </VideoPlayerProvider>
        )}
        {!collection && outofdate && !loading && (
          <React.Fragment>
            <LayoutContainer
              themes={["layout_bg_white"]}
              taxonomies={["layout.layout_spacing_lg"]}
            >
              <Container>
                <Box>
                  <TextModule
                    sectionTitle={""}
                    headline={
                      get(
                        DICTIONARY_MAP.digital_memories
                          .no_digital_memories_headline,
                        currentCulture
                      ) ?? ""
                    }
                    content={
                      get(
                        DICTIONARY_MAP.digital_memories
                          .no_digital_memories_text,
                        currentCulture
                      ) ?? ""
                    }
                    key={"there-are-no-bottles"}
                    link={{
                      name:
                        get(
                          DICTIONARY_MAP.digital_memories
                            .no_digital_memories_link_text,
                          currentCulture
                        ) ?? "",
                      url:
                        get(
                          DICTIONARY_MAP.digital_memories
                            .no_digital_memories_link_url,
                          currentCulture
                        ) ?? "",
                      relativeUrl: "",
                      urlSegment: "",
                      target: "_blank",
                    }}
                    cbKey={generateCbKey()}
                    shortCodes={[]}
                    themes={["text_color_red", "text_centering"]}
                    taxonomies={[]}
                    alias="textModuleElement"
                  />
                </Box>
              </Container>
            </LayoutContainer>
            <GoToKioskModule
              texts={{
                headline:
                  get(
                    DICTIONARY_MAP.digital_memories
                      .digital_memories_bottles_collection_headline,
                    currentCulture
                  ) ?? "",
                linkText:
                  get(
                    DICTIONARY_MAP.digital_memories
                      .digital_memories_bottles_collection_link_text,
                    currentCulture
                  ) ?? "",
                linkUrl:
                  get(
                    DICTIONARY_MAP.digital_memories
                      .digital_memories_bottles_collection_link_url,
                    currentCulture
                  ) ?? "",
              }}
            />
          </React.Fragment>
        )}
        {show && (
          <Portal domElementId={"beerDetail"}>
            <BottleDetails
              totalHits={bottles.length}
              hits={bottles}
              currentBottleIndex={currentBottleIndex}
              handleBottleIndex={handleBottleIndex}
              toggleDetail={toggleDetail}
              show={show}
              isLastPage={true}
              from={"memories"}
            />
          </Portal>
        )}
      </Box>
    );
  } else {
    return (
      <Portal domElementId={"EntryMoudle"}>
        <CodeModule />
      </Portal>
    );
  }
};

export { MyBottleCollection };
